import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const MyBookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Silakan login terlebih dahulu');
      navigate('/login');
      return;
    }

    const userStr = localStorage.getItem('user');
    if (!userStr) {
      toast.error('Silakan login terlebih dahulu');
      navigate('/login');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      loadBookings(user.id);
    } catch (error) {
      toast.error('Silakan login terlebih dahulu');
      navigate('/login');
    }
  }, [navigate]);

  const loadBookings = async (userId) => {
    try {
      const response = await api.get(`/bookings/user/${userId}`);
      setBookings(response.data);
    } catch (error) {
      toast.error('Gagal memuat data booking');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Pending', class: 'bg-secondary/10 text-secondary border-secondary/20' },
      confirmed: { text: 'Terkonfirmasi', class: 'bg-primary/10 text-primary border-primary/20' },
      cancelled: { text: 'Dibatalkan', class: 'bg-zinc-700 text-zinc-400 border-zinc-600' },
      expired: { text: 'Expire', class: 'bg-zinc-700 text-zinc-300 border-zinc-600' },
      expire: { text: 'Expire', class: 'bg-zinc-700 text-zinc-300 border-zinc-600' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase border ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Zero Futsal</span>
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Kembali</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-8">Booking Saya</h1>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500 text-lg mb-6">Anda belum memiliki booking</p>
              <Link
                to="/"
                className="inline-block bg-primary text-black px-8 py-3 rounded-md font-bold uppercase tracking-wide hover:bg-primary-hover transition-all"
              >
                Mulai Booking
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {bookings.map((item) => (
                <motion.div
                  key={item.booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`booking-item-${item.booking.id}`}
                  className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6 hover:shadow-xl transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">{item.venue?.name}</h3>
                      <p className="text-sm text-zinc-400">Booking ID: {item.booking.id.slice(0, 8)}</p>
                    </div>
                    {getStatusBadge(item.booking.status)}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-primary" />
                      <div>
                        <div className="text-xs text-zinc-400">Tanggal</div>
                        <div className="text-white font-bold">{item.booking.booking_date}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <div>
                        <div className="text-xs text-zinc-400">Jam</div>
                        <div className="text-white font-bold">
                          {Array.isArray(item.booking.time_slots)
                            ? item.booking.time_slots.length > 1 
                              ? `${item.booking.time_slots.length} slot` 
                              : item.booking.time_slots[0]
                            : item.booking.time_slot || '-'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-primary" />
                      <div>
                        <div className="text-xs text-zinc-400">Total</div>
                        <div className="text-white font-bold">
                          Rp {Number(item.computed_total_amount || 0).toLocaleString('id-ID')}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        {item.payment?.status === 'verified' ? '✓' : '⏳'}
                      </div>
                      <div>
                        <div className="text-xs text-zinc-400">Pembayaran</div>
                        <div className="text-white font-bold text-sm">
                          {item.payment?.status === 'verified' ? 'Verified' :
                           item.payment?.status === 'rejected' ? 'Rejected' : 'Pending'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/confirmation/${item.booking.id}`}
                    data-testid={`view-detail-${item.booking.id}`}
                    className="inline-block bg-transparent border-2 border-primary text-primary hover:bg-primary/10 px-6 py-2 rounded-md font-bold uppercase text-sm transition-all"
                  >
                    Lihat Detail
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MyBookingsPage;
